import { Choice, ChoiceType } from "../../simulation-decisions/types"
import { SavePointData, SavePointLogType } from "../../simulation/types"
import { CollaborativeChoice, CollaborationState, TranscriptionSegment } from "../types"
import { choiceToEvent, CrisisStatus } from "../../simulation/utils"

/**
 * Enhanced SavePointData type that includes collaborative session data
 */
export type EnhancedSavePointData = SavePointData & {
  collaborativeSession?: {
    roomId?: string
    roomName?: string
    participants: Array<{
      id: string
      userId: number
      name: string
      role: string
      joinedAt: string
      status: string
    }>
    votingThreshold?: string
    totalChoicesProposed: number
    totalVotesCast: number
    dataPrivacyNote: string
    transcriptionData?: {
      sessionTranscript: TranscriptionSegment[]
      transcriptionMetadata: {
        totalSegments: number
        averageConfidence: number
        participantSpeakingTime: Record<string, number>
        transcriptionDuration: string
        errorCount: number
        speakerIdentification: boolean
      }
    }
    communicationData?: {
      publicMessages: number
      privateMessages: number
      privateConversations: number
      totalCommunications: number
      messageBreakdown: {
        public: number
        private: number
      }
    }
  }
  analyticsReport?: {
    collaborationMetrics: {
      totalParticipants: number
      averageParticipationRate: number
      decisionMakingEfficiency: number
      consensusRate: number
    }
    decisionBreakdown: {
      strategic: number
      operational: number
      informational: number
    }
    communicationAnalysis?: {
      totalSpokenWords: number
      averageSegmentLength: number
      participantContribution: Record<string, {
        wordCount: number
        segmentCount: number
        averageConfidence: number
        speakingTimeMinutes: number
      }>
      messagingAnalysis: {
        publicMessages: number
        privateMessages: number
        privateConversations: number
        averageConversationSize: number
        participantMessagingActivity: Record<string, {
          publicMessages: number
          privateMessages: number
          conversationsInitiated: number
          conversationsParticipated: number
        }>
      }
    }
    timelineAnalysis: Array<{
      id: string
      proposedAt: string
      resolvedAt: string
      choiceType: ChoiceType
      outcome: string
      participationCount: number
    }>
  }
}

/**
 * Merges collaborative session data with main simulation data for comprehensive JSON export
 */
export const mergeCollaborativeData = (
  simulationData: SavePointData,
  collabState: CollaborationState,
  secondsElapsed: number
): EnhancedSavePointData => {
  const enhancedData: EnhancedSavePointData = { ...simulationData }

  // Calculate transcription metadata
  const transcriptionMetadata = calculateTranscriptionMetadata(collabState)
  
  // Calculate communication analytics
  const publicMessages = collabState.chatMessages.filter(msg => msg.messageType === "public").length
  const privateMessages = collabState.chatMessages.filter(msg => msg.messageType === "private").length
  const privateConversations = collabState.privateConversations.length

  // Add collaborative metadata
  enhancedData.collaborativeSession = {
    roomId: collabState.currentRoom?.id,
    roomName: collabState.currentRoom?.name,
    participants: collabState.participants.map(p => ({
      id: p.id,
      userId: p.userId,
      name: `${p.firstName} ${p.lastName}`,
      role: p.role,
      joinedAt: p.joinedAt,
      status: p.status
    })),
    votingThreshold: collabState.currentRoom?.votingThreshold,
    totalChoicesProposed: collabState.pendingChoices.length + collabState.completedChoices.length,
    totalVotesCast: collabState.completedChoices.reduce((total, choice) => total + choice.votes.length, 0),
    dataPrivacyNote: "Individual information searches are private to each user and not shared with the team unless explicitly chosen by the user. Private messages are only visible to intended recipients.",
    transcriptionData: {
      sessionTranscript: collabState.transcription.segments,
      transcriptionMetadata
    },
    communicationData: {
      publicMessages,
      privateMessages,
      privateConversations,
      totalCommunications: publicMessages + privateMessages,
      messageBreakdown: {
        public: publicMessages,
        private: privateMessages
      }
    }
  }

  // Convert collaborative choices to simulation events and merge with log
  // NOTE: Individual actions (like information searches) are NOT included here
  // They remain private to the individual user and appear only in their personal log
  const collaborativeEvents: SavePointLogType[] = []

  const allChoices = [...collabState.completedChoices, ...collabState.pendingChoices]
  allChoices.forEach(collabChoice => {
    // Create main decision event
    const decisionEvent = choiceToEvent(
      collabChoice.choice,
      secondsElapsed,
      enhancedData.log.filter(item => item.type === "decision"),
      CrisisStatus.PRE_CRISIS // crisis status (normal/pre-crisis state)
    )

    // Enhance with collaborative data
    const enhancedEvent = {
      ...decisionEvent,
      collaborativeData: {
        proposedBy: collabChoice.proposedByName,
        proposedAt: collabChoice.proposedAt,
        votingDeadline: collabChoice.votingDeadline,
        requiredThreshold: collabChoice.requiredThreshold,
        status: collabChoice.status,
        votes: collabChoice.votes.map(vote => ({
          participantId: vote.participantId,
          participantName: collabState.participants.find(p => p.id === vote.participantId)?.firstName + ' ' + 
                          collabState.participants.find(p => p.id === vote.participantId)?.lastName,
          decision: vote.decision,
          timestamp: vote.timestamp
        })),
        voteSummary: {
          totalVotes: collabChoice.votes.length,
          approve: collabChoice.votes.filter(v => v.decision === 'approve').length,
          reject: collabChoice.votes.filter(v => v.decision === 'reject').length,
          abstain: collabChoice.votes.filter(v => v.decision === 'abstain').length
        }
      }
    }

    collaborativeEvents.push(enhancedEvent)
  })

  // Add chat messages as communication events
  const chatEvents: SavePointLogType[] = collabState.chatMessages.map(msg => ({
    id: parseInt(msg.id.replace(/[^0-9]/g, '')) || Math.floor(Math.random() * 1000000),
    type: msg.messageType === "public" ? "team_communication" : "private_communication",
    title: msg.messageType === "public" ? "Team Chat" : "Private Message",
    description: msg.message,
    elapsedTime: msg.timestamp,
    collaborativeData: {
      messageType: msg.messageType,
      userId: msg.userId,
      userName: msg.userName,
      roomId: msg.roomId,
      recipientIds: msg.recipientIds,
      conversationId: msg.conversationId,
      participantCount: msg.recipientIds ? msg.recipientIds.length + 1 : undefined // +1 for sender
    }
  } as unknown as SavePointLogType))

  // Add private conversation metadata
  const conversationEvents: SavePointLogType[] = collabState.privateConversations.map((conv, index) => ({
    id: 20000 + index, // Use high numbers to avoid ID conflicts
    type: "conversation_created",
    title: "Private Conversation Created",
    description: `Conversation started with ${conv.participantNames.join(', ')}`,
    elapsedTime: conv.createdAt,
    collaborativeData: {
      conversationId: conv.id,
      participantIds: conv.participantIds,
      participantNames: conv.participantNames,
      participantCount: conv.participantIds.length
    }
  } as unknown as SavePointLogType))

  // Add transcription segments as communication events (for timeline analysis)
  const transcriptionEvents: SavePointLogType[] = collabState.transcription.segments.map((segment, index) => ({
    id: 10000 + index, // Use high numbers to avoid ID conflicts
    type: "voice_communication",
    title: "Voice Transcription",
    description: segment.text,
    elapsedTime: segment.timestamp,
    collaborativeData: {
      messageType: "transcription",
      participantId: segment.participantId,
      speaker: segment.speaker,
      confidence: segment.confidence,
      duration: segment.end - segment.start,
      wordCount: segment.words.length
    }
  } as unknown as SavePointLogType))

  // Merge all events chronologically
  enhancedData.log = [
    ...enhancedData.log,
    ...collaborativeEvents,
    ...chatEvents,
    ...conversationEvents,
    ...transcriptionEvents
  ].sort((a, b) => new Date(a.elapsedTime || 0).getTime() - new Date(b.elapsedTime || 0).getTime())

  return enhancedData
}

/**
 * Enhanced Choice type with collaborative metadata
 */
export type EnhancedChoice = Choice & {
  collaborativeMetadata?: {
    isCollaborative: boolean
    proposedBy: string
    votingStatus: string
    votes: Array<{ participantId: string; decision: string; timestamp: string }>
  }
}

/**
 * Converts a collaborative choice to a main simulation choice for immediate processing
 */
export const convertCollaborativeToSimulationChoice = (
  collaborativeChoice: CollaborativeChoice
): EnhancedChoice => {
  return {
    ...collaborativeChoice.choice,
    collaborativeMetadata: {
      isCollaborative: true,
      proposedBy: collaborativeChoice.proposedBy,
      votingStatus: collaborativeChoice.status,
      votes: collaborativeChoice.votes
    }
  }
}

/**
 * Creates comprehensive final results JSON for collaborative simulation
 */
export const createCollaborativeFinalResults = (
  simulationData: SavePointData,
  collabState: CollaborationState
): EnhancedSavePointData => {
  const finalResults = mergeCollaborativeData(simulationData, collabState, 0)
  
  // Add summary analytics
  finalResults.analyticsReport = {
    collaborationMetrics: {
      totalParticipants: collabState.participants.length,
      averageParticipationRate: calculateParticipationRate(collabState),
      decisionMakingEfficiency: calculateDecisionEfficiency(collabState),
      consensusRate: calculateConsensusRate(collabState)
    },
    decisionBreakdown: categorizeDecisions(collabState),
    communicationAnalysis: calculateCommunicationAnalysis(collabState),
    timelineAnalysis: analyzeDecisionTimeline(collabState)
  }

  return finalResults
}

// Helper functions for analytics
const calculateParticipationRate = (state: CollaborationState): number => {
  const totalChoices = state.completedChoices.length
  if (totalChoices === 0) return 0
  
  const totalPossibleVotes = totalChoices * state.participants.length
  const actualVotes = state.completedChoices.reduce((sum, choice) => sum + choice.votes.length, 0)
  
  return (actualVotes / totalPossibleVotes) * 100
}

const calculateDecisionEfficiency = (state: CollaborationState): number => {
  // Average time from proposal to resolution
  const resolvedChoices = state.completedChoices.filter(c => c.votingDeadline)
  if (resolvedChoices.length === 0) return 0
  
  const avgTime = resolvedChoices.reduce((sum, choice) => {
    const proposalTime = new Date(choice.proposedAt).getTime()
    const deadlineTime = new Date(choice.votingDeadline).getTime()
    return sum + (deadlineTime - proposalTime)
  }, 0) / resolvedChoices.length
  
  return avgTime / (1000 * 60) // Convert to minutes
}

const calculateConsensusRate = (state: CollaborationState): number => {
  const votedChoices = state.completedChoices.filter(c => c.votes.length > 0)
  if (votedChoices.length === 0) return 0
  
  const consensusChoices = votedChoices.filter(choice => {
    const approvals = choice.votes.filter(v => v.decision === 'approve').length
    const total = choice.votes.length
    return approvals / total >= 0.8 // 80% consensus threshold
  })
  
  return (consensusChoices.length / votedChoices.length) * 100
}

const categorizeDecisions = (state: CollaborationState) => {
  const categories = {
    strategic: 0,
    operational: 0,
    informational: 0
  }
  
  state.completedChoices.forEach(choice => {
    switch (choice.choice.choiceType) {
      case ChoiceType.decision:
        categories.strategic++
        break
      case ChoiceType.plan:
        categories.operational++
        break
      case ChoiceType.search:
        categories.informational++
        break
    }
  })
  
  return categories
}

const analyzeDecisionTimeline = (state: CollaborationState) => {
  return state.completedChoices.map(choice => ({
    id: choice.id,
    proposedAt: choice.proposedAt,
    resolvedAt: choice.votingDeadline,
    choiceType: choice.choice.choiceType,
    outcome: choice.status,
    participationCount: choice.votes.length
  }))
}

// New helper functions for transcription analysis
const calculateTranscriptionMetadata = (state: CollaborationState) => {
  const segments = state.transcription.segments
  
  if (segments.length === 0) {
    return {
      totalSegments: 0,
      averageConfidence: 0,
      participantSpeakingTime: {},
      transcriptionDuration: "00:00:00",
      errorCount: state.transcription.transcriptionErrors.length,
      speakerIdentification: false
    }
  }

  // Calculate average confidence
  const averageConfidence = segments.reduce((sum, segment) => sum + segment.confidence, 0) / segments.length

  // Calculate speaking time per participant
  const participantSpeakingTime: Record<string, number> = {}
  segments.forEach(segment => {
    const participantKey = segment.participantId || 'unknown'
    const duration = segment.end - segment.start
    participantSpeakingTime[participantKey] = (participantSpeakingTime[participantKey] || 0) + duration
  })

  // Calculate total duration
  const totalDuration = segments.reduce((total, segment) => total + (segment.end - segment.start), 0)
  const hours = Math.floor(totalDuration / 3600)
  const minutes = Math.floor((totalDuration % 3600) / 60)
  const seconds = Math.floor(totalDuration % 60)
  const transcriptionDuration = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`

  return {
    totalSegments: segments.length,
    averageConfidence: Math.round(averageConfidence * 100) / 100,
    participantSpeakingTime,
    transcriptionDuration,
    errorCount: state.transcription.transcriptionErrors.length,
    speakerIdentification: segments.some(s => s.speaker !== undefined)
  }
}

const calculateCommunicationAnalysis = (state: CollaborationState) => {
  const segments = state.transcription.segments
  
  // Calculate messaging analytics
  const publicMessages = state.chatMessages.filter(msg => msg.messageType === "public").length
  const privateMessages = state.chatMessages.filter(msg => msg.messageType === "private").length
  const privateConversations = state.privateConversations.length
  
  const averageConversationSize = privateConversations > 0 
    ? state.privateConversations.reduce((sum, conv) => sum + conv.participantIds.length, 0) / privateConversations
    : 0

  // Calculate participant messaging activity
  const participantMessagingActivity: Record<string, { publicMessages: number; privateMessages: number; conversationsInitiated: number; conversationsParticipated: number }> = {}

  state.participants.forEach(participant => {
    const participantName = `${participant.firstName} ${participant.lastName}`
    const userPublicMessages = state.chatMessages.filter(msg =>
      msg.userId === participant.userId && msg.messageType === "public"
    ).length

    const userPrivateMessages = state.chatMessages.filter(msg =>
      msg.userId === participant.userId && msg.messageType === "private"
    ).length

    const conversationsInitiated = state.privateConversations.filter(conv =>
      conv.participantIds[0] === participant.userId // Assuming first participant is the initiator
    ).length

    const conversationsParticipated = state.privateConversations.filter(conv =>
      conv.participantIds.includes(participant.userId)
    ).length

    participantMessagingActivity[participantName] = {
      publicMessages: userPublicMessages,
      privateMessages: userPrivateMessages,
      conversationsInitiated,
      conversationsParticipated
    }
  })
  
  if (segments.length === 0) {
    return {
      totalSpokenWords: 0,
      averageSegmentLength: 0,
      participantContribution: {},
      messagingAnalysis: {
        publicMessages,
        privateMessages,
        privateConversations,
        averageConversationSize: Math.round(averageConversationSize * 100) / 100,
        participantMessagingActivity
      }
    }
  }

  // Calculate total spoken words
  const totalSpokenWords = segments.reduce((total, segment) => total + segment.words.length, 0)

  // Calculate average segment length
  const averageSegmentLength = totalSpokenWords / segments.length

  // Calculate participant contributions
  const participantContribution: Record<string, { wordCount: number; segmentCount: number; averageConfidence: number; speakingTimeMinutes: number }> = {}

  state.participants.forEach(participant => {
    const participantId = participant.userId.toString()
    const participantSegments = segments.filter(s => s.participantId === participantId)

    if (participantSegments.length > 0) {
      const wordCount = participantSegments.reduce((total, segment) => total + segment.words.length, 0)
      const averageConfidence = participantSegments.reduce((sum, segment) => sum + segment.confidence, 0) / participantSegments.length
      const speakingTimeSeconds = participantSegments.reduce((total, segment) => total + (segment.end - segment.start), 0)

      participantContribution[`${participant.firstName} ${participant.lastName}`] = {
        wordCount,
        segmentCount: participantSegments.length,
        averageConfidence: Math.round(averageConfidence * 100) / 100,
        speakingTimeMinutes: Math.round((speakingTimeSeconds / 60) * 100) / 100
      }
    }
  })

  return {
    totalSpokenWords,
    averageSegmentLength: Math.round(averageSegmentLength * 100) / 100,
    participantContribution,
    messagingAnalysis: {
      publicMessages,
      privateMessages,
      privateConversations,
      averageConversationSize: Math.round(averageConversationSize * 100) / 100,
      participantMessagingActivity
    }
  }
} 